import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { CardHeader } from '../ui/CardHeader';
import { Pill } from '../ui/Pill';
import { StatTile } from '../ui/StatTile';
import { SurfaceCard } from '../ui/SurfaceCard';
import type { WeatherView } from '../../weather/format';

interface AirCardProps {
  view: WeatherView;
  onOpenHistory: () => void;
}

export function AirCard({ view, onOpenHistory }: AirCardProps) {
  return (
    <SurfaceCard>
      <CardHeader title="Aer" onOpenHistory={onOpenHistory} />

      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: 'clamp(20px, 3vw, 44px)',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '6px', lineHeight: 1 }}>
          <Typography
            component="span"
            sx={{
              fontWeight: 300,
              fontSize: 'clamp(74px, 10vw, 118px)',
              letterSpacing: '-.05em',
              lineHeight: 0.86,
            }}
          >
            {view.temperature}
          </Typography>
          <Typography
            component="span"
            sx={(theme) => ({
              fontSize: 'clamp(22px, 2.4vw, 32px)',
              fontWeight: 400,
              color: theme.palette.weather.faint,
              marginTop: '.2em',
            })}
          >
            °C
          </Typography>
          {view.hasCondition && (
            <Box sx={{ marginTop: '.5em' }}>
              <Pill tone="accent">{view.condition}</Pill>
            </Box>
          )}
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <Box sx={{ position: 'relative', width: 96, height: 96, flex: 'none' }}>
            <Box
              component="svg"
              viewBox="0 0 120 120"
              aria-hidden
              sx={{ width: 96, height: 96, display: 'block' }}
            >
              <Box
                component="circle"
                cx="60"
                cy="60"
                r="50"
                fill="none"
                strokeWidth="10"
                sx={(theme) => ({ stroke: theme.palette.weather.line })}
              />
              <Box
                component="circle"
                cx="60"
                cy="60"
                r="50"
                fill="none"
                strokeWidth="10"
                strokeLinecap="round"
                strokeDasharray={view.humArc}
                transform="rotate(-90 60 60)"
                sx={(theme) => ({
                  stroke: theme.palette.weather.accent,
                  transition: 'stroke-dasharray 1s ease, stroke .5s ease',
                })}
              />
            </Box>
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 19,
                fontWeight: 500,
              }}
            >
              {view.humidity}
            </Box>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <Typography
              component="span"
              sx={(theme) => ({
                fontSize: 12,
                fontWeight: 500,
                color: theme.palette.weather.faint,
              })}
            >
              Umiditate
            </Typography>
            <Typography
              component="span"
              sx={(theme) => ({ fontSize: 13, color: theme.palette.weather.soft })}
            >
              Punct de rouă {view.dewPoint}
            </Typography>
          </Box>
        </Box>
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
          gap: '12px',
        }}
      >
        <StatTile label="Resimțit" value={view.apparentTemperature} />
        <StatTile label="Umezeală resimțită" value={view.humidex} />
        <StatTile label="Răcire eoliană" value={view.windChill} />
        <StatTile label="Confort termic" value={view.heatStress} />
      </Box>
    </SurfaceCard>
  );
}
